import { useIntl } from 'react-intl'
import clsx from 'clsx'
import { Box, styled, Tooltip } from '@mui/material'
import { IconButton } from '@carto/meridian-ds/components'
import { AddOutlined, RemoveOutlined } from '@mui/icons-material'
import CircularProgress from '@mui/material/CircularProgress'

export const MAXIMUM_ZOOM_LEVEL = 20
export const MINIMUN_ZOOM_LEVEL = 1

const ZoomControlWrapper = styled('div')(({ theme }) => ({
  userSelect: 'none',
  msUserSelect: 'none',
  WebkitUserSelect: 'none',
  MozUserSelect: 'none',
  position: 'absolute',
  left: theme.spacing(3),
  bottom: theme.spacing(3),
  display: 'flex',
  flexDirection: 'row',
  zIndex: 9,
  borderRadius: theme.spacing(0.5),
  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.16), 0 2px 1px -1px rgba(0, 0, 0, 0.04), 0 1px 1px 0 rgba(0, 0, 0, 0.16)',
  backgroundColor: theme.palette.common.white,

  '& > div': {
    alignItems: 'center',
    display: 'flex',
    fontSize: theme.spacing(1.5),
    fontWeight: 600,
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: 1.33,
    letterSpacing: '0.2px',
    color: theme.palette.grey[600],
    height: theme.spacing(4),
    justifyContent: 'center',
    width: theme.spacing(4)
  },
  '& > div:not(:last-of-type)': {
    borderRight: `1px solid rgba(0, 0, 0, 0.12)`
  },
  '& > div:last-of-type, & > div:first-of-type': {
    cursor: 'pointer'
  },
  '&.vertical': {
    flexDirection: 'column-reverse',
    '& > div:not(:first-of-type)': {
      borderBottom: `1px solid rgba(0, 0, 0, 0.12)`
    },
    '& > div:not(:last-of-type)': {
      borderRight: 'none'
    }
  },
  transition: theme.transitions.create(['left'], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.standard
  })
}))

const LoadingWrapper = styled('div')(() => ({
  position: 'absolute',
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}))

const ZoomIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'orientation'
})<{ orientation?: 'vertical' | 'horizontal' }>(({ theme, orientation }) => ({
  '&:first-of-type button': {
    borderRadius: `${theme.shape.borderRadius}px 0 0 ${theme.shape.borderRadius}px`
  },
  '&:last-of-type button': {
    borderRadius: `0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0`
  },

  ...(orientation === 'vertical' && {
    '&:first-of-type button': {
      borderRadius: `0 0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px`
    },
    '&:last-of-type button': {
      borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`
    }
  })
}))

function ZoomControl({
  zoom,
  minZoom = MINIMUN_ZOOM_LEVEL,
  maxZoom = MAXIMUM_ZOOM_LEVEL,
  zoomChanged,
  orientation = 'horizontal',
  isLoading = false,
  loadingText = '',
  className
}: {
  zoom: number
  minZoom?: number
  maxZoom?: number
  zoomChanged: { (newZoom: number): void }
  orientation?: 'vertical' | 'horizontal'
  isLoading?: boolean
  loadingText?: string
  className?: string
}) {
  const intl = useIntl()
  const zoomInLabel = intl.formatMessage({ id: 'builder.controls.zoomIn' })
  const zoomOutLabel = intl.formatMessage({ id: 'builder.controls.zoomOut' })
  const loadingLabel = loadingText || intl.formatMessage({ id: 'builder.loading.tiles' })
  const roundedZoomLabel = intl.formatMessage({ id: 'builder.controls.roundedZoom' })
  const roundedZoom = Math.round(zoom)
  const isHorizontal = orientation === 'horizontal'
  const placement = isHorizontal ? 'top' : 'right'

  const zoomOut = () => {
    const nextZoom = roundedZoom - 1
    if (nextZoom >= minZoom) {
      zoomChanged(nextZoom)
    }
  }

  const zoomIn = () => {
    const nextZoom = roundedZoom + 1
    if (nextZoom <= maxZoom) {
      zoomChanged(nextZoom)
    }
  }

  return (
    <ZoomControlWrapper className={clsx(orientation, 'map-thumbnail-hidden', className)} data-testid="map-controls">
      <ZoomIconButton
        onClick={zoomOut}
        disabled={roundedZoom === minZoom}
        icon={<RemoveOutlined />}
        tooltip={zoomOutLabel}
        tooltipPlacement={placement}
        orientation={orientation}
      />
      <Box position="relative">
        {isLoading && (
          <LoadingWrapper aria-label={loadingLabel}>
            <Tooltip title={loadingText} placement={placement}>
              <CircularProgress color="primary" size={24} />
            </Tooltip>
          </LoadingWrapper>
        )}
        <span aria-label={roundedZoomLabel}>{roundedZoom}</span>
      </Box>
      <ZoomIconButton
        onClick={zoomIn}
        icon={<AddOutlined />}
        tooltip={zoomInLabel}
        tooltipPlacement={placement}
        orientation={orientation}
      />
    </ZoomControlWrapper>
  )
}

ZoomControl.displayName = 'ZoomControl'

export default ZoomControl
